import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { Series, InsertSeries } from "../lib/supabase";
import SeriesForm from "../components/series/SeriesForm";
import toast from "react-hot-toast";
import LoadingScreen from "../components/ui/LoadingScreen";
import { useSeries } from "../contexts/SeriesContext";
import SmartBackButton from "../components/ui/SmartBackButton";

const EditSeries: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [series, setSeries] = useState<Series | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const navigate = useNavigate();
  const { refreshSeries } = useSeries();

  useEffect(() => {
    const fetchSeries = async () => {
      try {
        setIsFetching(true);
        if (!id) return;

        // Fetch series data
        const { data: seriesData, error: seriesError } = await supabase
          .from("series")
          .select("*")
          .eq("id", id)
          .single();

        if (seriesError) throw seriesError;

        // Fetch associated labs
        const { data: labsData, error: labsError } = await supabase
          .from("labs")
          .select("*")
          .eq("series_id", id)
          .order("order_number");

        if (labsError) throw labsError;

        // Combine series and labs data
        setSeries({
          ...seriesData,
          labs: labsData,
        });
      } catch (error) {
        console.error("Error fetching series:", error);
        toast.error("Failed to load series details");
      } finally {
        setIsFetching(false);
      }
    };
    fetchSeries();
  }, [id]);

  const handleUpdateSeries = async (formData: InsertSeries | Series) => {
    try {
      setIsLoading(true);
      if (!id) return;

      // Extract labs and prepare series update data
      const { id: _, created_at: __, labs, ...updateFields } = formData as any;

      // Update series data
      const { error: seriesError } = await supabase
        .from("series")
        .update(updateFields)
        .eq("id", id);

      if (seriesError) throw seriesError;

      // Handle labs updates
      if (labs && labs.length > 0) {
        // First, delete all existing labs
        const { error: deleteError } = await supabase
          .from("labs")
          .delete()
          .eq("series_id", id);

        if (deleteError) throw deleteError;

        // Then insert the new labs
        const { error: labsError } = await supabase.from("labs").insert(
          labs.map((lab: any) => ({
            ...lab,
            series_id: id,
          }))
        );

        if (labsError) throw labsError;
      }

      // After successful update, refresh the series data
      const updatedSeries = await refreshSeries(id);
      if (updatedSeries) {
        setSeries(updatedSeries);
      }

      toast.success("Series updated successfully");
      navigate(`/series/${id}`);
    } catch (error) {
      console.error("Error updating series:", error);
      toast.error("Failed to update series");
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) return <LoadingScreen />;

  if (!series) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-semibold text-neutral-800 mb-4">
          Series Not Found
        </h2>
        <p className="text-neutral-600 mb-6">
          The series you are trying to edit does not exist or has been removed.
        </p>
        <SmartBackButton fallbackPath="/series" className="btn btn-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton fallbackPath={`/series/${id}`} />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1 className="">Edit {series.code}</h1>
          </div>

          {/* Workflow guidance */}
          <div className="">
            <p className="mb-6">
              Groups and labs can be added or edit from the Series Detail page
              or directly from the Groups and Labs sections.
            </p>
          </div>

          <SeriesForm
            defaultValues={series}
            onSubmit={handleUpdateSeries}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EditSeries;
